import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  assignPlanToClient,
  evaluatePlanAssignmentEligibility,
  getAssignableClients,
  getPlanAssignmentCoverage,
  getPlanById,
  getPlanTypeLabel,
  getPlanUsage,
  publishAndAssignPlan,
  removeAlternativePlan,
  unassignPrimaryPlanFromClient,
  trackTrainingPlansV2Event,
} from "./services/trainingPlansV2Service";
import { useTrainingPlansV2Hints } from "./useTrainingPlansV2Hints";

const getCoverageChipSx = (count, activeColor) =>
  count > 0
    ? (theme) => ({
        color: theme.palette[activeColor].main,
        borderColor: theme.palette[activeColor].main,
        backgroundColor: alpha(theme.palette[activeColor].main, 0.12),
        "& .MuiChip-label": { fontWeight: 600 },
      })
    : undefined;

const steps = ["Select Target", "Assignment Mode", "Review & Confirm"];

/** Order used for default mode and the Assignment Mode dropdown. */
const ASSIGNMENT_MODE_ORDER = ["active", "alternative", "variant", "variant_draft"];

const ASSIGNMENT_MODE_LABELS = {
  active: "Set as Active Plan",
  alternative: "Add as Alternative Plan",
  variant: "Create Special Variant and Assign",
  variant_draft: "Create Variant as Draft (do not change primary)",
};

const TrainingPlansV2Assign = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { planId } = useParams();
  const hints = useTrainingPlansV2Hints("assign");
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = React.useState(0);
  const [plan, setPlan] = React.useState(null);
  const [clients, setClients] = React.useState([]);
  const [selectedClientId, setSelectedClientId] = React.useState("");
  const [hasUserSelectedClient, setHasUserSelectedClient] = React.useState(false);
  const [selectedAlternativeClientIds, setSelectedAlternativeClientIds] = React.useState([]);
  const [mode, setMode] = React.useState("active");
  const [coachNote, setCoachNote] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [messageSeverity, setMessageSeverity] = React.useState("success");
  const [usage, setUsage] = React.useState([]);
  const [assignmentCoverage, setAssignmentCoverage] = React.useState(null);
  const [removingAlternativeClientId, setRemovingAlternativeClientId] = React.useState("");
  const [removingPrimaryClientId, setRemovingPrimaryClientId] = React.useState("");
  const requestedClientId = searchParams.get("clientUid") || "";

  const refreshCurrentAssignments = React.useCallback(async () => {
    const [usageValue, clientsValue] = await Promise.all([
      getPlanUsage(planId),
      getAssignableClients(),
    ]);
    setUsage(usageValue || []);
    setClients(clientsValue || []);
    const cov = await getPlanAssignmentCoverage({
      planIds: [planId],
      clients: clientsValue || [],
    });
    setAssignmentCoverage(cov?.[planId] ?? null);
  }, [planId]);

  React.useEffect(() => {
    async function load() {
      const [planValue, clientsValue, usageValue] = await Promise.all([
        getPlanById(planId),
        getAssignableClients(),
        getPlanUsage(planId),
      ]);
      setPlan(planValue);
      setClients(clientsValue || []);
      setUsage(usageValue || []);
    }
    load();
  }, [planId]);

  React.useEffect(() => {
    async function loadCoverage() {
      if (!planId || !clients.length) {
        setAssignmentCoverage(null);
        return;
      }
      const cov = await getPlanAssignmentCoverage({
        planIds: [planId],
        clients,
      });
      setAssignmentCoverage(cov?.[planId] ?? null);
    }
    loadCoverage();
  }, [planId, clients]);

  const sortedClients = React.useMemo(
    () =>
      [...clients].sort((a, b) => {
        const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.uid || "";
        const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim() || b.uid || "";
        return aName.localeCompare(bName);
      }),
    [clients],
  );
  const clientsById = React.useMemo(
    () =>
      clients.reduce((acc, client) => {
        const key = client?.uid || client?.id;
        if (key) {
          acc[key] = client;
        }
        return acc;
      }, {}),
    [clients],
  );

  const primaryAssignments = React.useMemo(() => {
    return usage.map((client) => {
      const identity = client?.uid || client?.id || "";
      const rosterClient = clientsById[identity] || {};
      const firstName = client?.firstName || rosterClient?.firstName || "";
      const lastName = client?.lastName || rosterClient?.lastName || "";
      const displayName = `${firstName} ${lastName}`.trim() || identity || "Unknown client";
      return {
        ...client,
        identity,
        displayName,
      };
    });
  }, [clientsById, usage]);

  const alternativeAssignments = React.useMemo(() => {
    return clients
      .filter((client) => {
        const alternatives = Array.isArray(client?.alternativeProtocols)
          ? client.alternativeProtocols
          : [];
        return alternatives.includes(planId);
      })
      .map((client) => {
        const identity = client?.uid || client?.id || "";
        const firstName = client?.firstName || "";
        const lastName = client?.lastName || "";
        const displayName = `${firstName} ${lastName}`.trim() || identity || "Unknown client";
        return {
          ...client,
          identity,
          displayName,
        };
      });
  }, [clients, planId]);

  const currentPlanCoverage = assignmentCoverage || {
    primaryCount: usage.length,
    alternativeCount: 0,
  };
  const selectedClient = React.useMemo(
    () => (selectedClientId ? clientsById[selectedClientId] || null : null),
    [clientsById, selectedClientId],
  );
  React.useEffect(() => {
    if (!plan?.clientUID) {
      return;
    }
    if (hasUserSelectedClient || selectedClientId) {
      return;
    }
    const canUseRequestedClient = !!requestedClientId && !!clientsById[requestedClientId];
    const canUseOwnerClient = !!clientsById[plan.clientUID];
    const defaultClientId = canUseRequestedClient
      ? requestedClientId
      : canUseOwnerClient
        ? plan.clientUID
        : "";
    if (defaultClientId) {
      setSelectedClientId(defaultClientId);
    }
  }, [clientsById, hasUserSelectedClient, plan, requestedClientId, selectedClientId]);
  const alternativeEligibilityByClientId = React.useMemo(
    () =>
      clients.reduce((acc, client) => {
        acc[client.uid] = evaluatePlanAssignmentEligibility({
          sourcePlan: plan,
          targetClientId: client.uid,
          mode: "alternative",
          createVariant: false,
        });
        return acc;
      }, {}),
    [clients, plan],
  );
  const eligibleAlternativeClients = React.useMemo(
    () =>
      sortedClients.filter(
        (client) => alternativeEligibilityByClientId[client.uid]?.allowed,
      ),
    [alternativeEligibilityByClientId, sortedClients],
  );
  const selectedAlternativeClients = React.useMemo(
    () =>
      selectedAlternativeClientIds
        .map((clientId) => clientsById[clientId])
        .filter(Boolean),
    [clientsById, selectedAlternativeClientIds],
  );
  const eligibilityByMode = React.useMemo(() => {
    return {
      active: evaluatePlanAssignmentEligibility({
        sourcePlan: plan,
        targetClientId: selectedClientId,
        mode: "active",
        createVariant: false,
      }),
      alternative: evaluatePlanAssignmentEligibility({
        sourcePlan: plan,
        targetClientId: selectedClientId,
        mode: "alternative",
        createVariant: false,
      }),
      variant: evaluatePlanAssignmentEligibility({
        sourcePlan: plan,
        targetClientId: selectedClientId,
        mode: "active",
        createVariant: true,
      }),
      variant_draft: evaluatePlanAssignmentEligibility({
        sourcePlan: plan,
        targetClientId: selectedClientId,
        mode: "active",
        createVariant: true,
      }),
    };
  }, [plan, selectedClientId]);
  const selectedModeEligibility = eligibilityByMode[mode] || { allowed: false };
  const isCrossOwnerClientPlanAssignment = Boolean(
    plan?.clientUID && selectedClientId && selectedClientId !== plan.clientUID,
  );
  const ownerClientLabel = React.useMemo(() => {
    const id = plan?.clientUID;
    if (!id) {
      return "";
    }
    const c = clientsById[id];
    const n = `${c?.firstName || ""} ${c?.lastName || ""}`.trim();
    return n || id;
  }, [plan?.clientUID, clientsById]);

  const availableModes = React.useMemo(() => {
    if (!plan || !selectedClientId) {
      return [];
    }
    const pick = [];
    if (eligibilityByMode.active?.allowed) {
      pick.push("active");
    }
    if (eligibilityByMode.alternative?.allowed && eligibleAlternativeClients.length > 0) {
      pick.push("alternative");
    }
    if (eligibilityByMode.variant?.allowed) {
      pick.push("variant");
    }
    if (eligibilityByMode.variant_draft?.allowed) {
      pick.push("variant_draft");
    }
    return ASSIGNMENT_MODE_ORDER.filter((key) => pick.includes(key));
  }, [
    plan,
    selectedClientId,
    eligibilityByMode.active?.allowed,
    eligibilityByMode.alternative?.allowed,
    eligibilityByMode.variant?.allowed,
    eligibilityByMode.variant_draft?.allowed,
    eligibleAlternativeClients.length,
  ]);

  const availableModesKey = availableModes.join("|");

  React.useLayoutEffect(() => {
    if (activeStep !== 1) {
      return;
    }
    if (!availableModesKey) {
      return;
    }
    const keys = availableModesKey.split("|");
    if (!keys.length) {
      return;
    }
    if (!keys.includes(mode)) {
      setMode(keys[0]);
    }
  }, [activeStep, availableModesKey, mode]);

  const planStatus = plan?.v2Meta?.status || plan?.status || "draft";
  const requiresPublishBeforeAssign = planStatus === "draft";
  const isLifecycleReadOnlyStatus = planStatus === "archived";
  const uniqueAlternativeTargets = React.useMemo(
    () => Array.from(new Set(selectedAlternativeClientIds.filter(Boolean))),
    [selectedAlternativeClientIds],
  );
  const hasInvalidAlternativeTargets = React.useMemo(
    () =>
      uniqueAlternativeTargets.some(
        (clientId) => !alternativeEligibilityByClientId[clientId]?.allowed,
      ),
    [alternativeEligibilityByClientId, uniqueAlternativeTargets],
  );
  const isStepOneValid = mode === "alternative" || !!selectedClientId;
  const isStepTwoValid =
    mode === "alternative"
      ? uniqueAlternativeTargets.length > 0 && !hasInvalidAlternativeTargets
      : !!selectedClientId && !!selectedModeEligibility.allowed;
  const isReadyToConfirm = isStepOneValid && isStepTwoValid;

  React.useEffect(() => {
    if (mode !== "alternative") {
      return;
    }
    if (!selectedClientId) {
      return;
    }
    setSelectedAlternativeClientIds((prev) => {
      if (prev.includes(selectedClientId)) {
        return prev;
      }
      return [...prev, selectedClientId];
    });
  }, [mode, selectedClientId]);

  const resetAssignmentFlow = React.useCallback(() => {
    setActiveStep(0);
    setSelectedClientId("");
    setHasUserSelectedClient(false);
    setSelectedAlternativeClientIds([]);
    setMode("active");
    setCoachNote("");
  }, []);

  const handleRemoveAlternativeAssignment = React.useCallback(
    async (assignment) => {
      const clientId = assignment?.identity;
      if (!clientId) {
        setMessageSeverity("error");
        setMessage("Missing client identity. Unable to remove alternative assignment.");
        return;
      }
      const displayName = assignment?.displayName || "this client";
      const shouldContinue = window.confirm(
        `Remove ${plan?.name || "this plan"} as an alternative assignment for ${displayName}?`,
      );
      if (!shouldContinue) {
        return;
      }
      setRemovingAlternativeClientId(clientId);
      try {
        await removeAlternativePlan(clientId, planId);
        await trackTrainingPlansV2Event("plan_alternative_assignment_remove", {
          planId,
          clientId,
        });
        setMessageSeverity("success");
        setMessage(`Removed alternative assignment for ${displayName}.`);
        await refreshCurrentAssignments();
      } catch (error) {
        setMessageSeverity("error");
        setMessage(error?.message || "Failed to remove alternative assignment.");
      } finally {
        setRemovingAlternativeClientId("");
      }
    },
    [plan?.name, planId, refreshCurrentAssignments],
  );

  const handleRemovePrimaryAssignment = React.useCallback(
    async (assignment) => {
      const clientId = assignment?.identity;
      if (!clientId) {
        setMessageSeverity("error");
        setMessage("Missing client identity. Unable to remove primary assignment.");
        return;
      }
      const displayName = assignment?.displayName || "this client";
      const shouldContinue = window.confirm(
        `Remove ${plan?.name || "this plan"} as the primary training plan for ${displayName}? They will have no assigned plan until you assign a new one.`,
      );
      if (!shouldContinue) {
        return;
      }
      setRemovingPrimaryClientId(clientId);
      try {
        await unassignPrimaryPlanFromClient({
          clientId,
          planId,
          sourcePlanName: plan?.name,
          clientNameSnapshot: displayName,
        });
        await trackTrainingPlansV2Event("plan_primary_assignment_remove", {
          planId,
          clientId,
        });
        setMessageSeverity("success");
        setMessage(`Removed primary assignment for ${displayName}.`);
        await refreshCurrentAssignments();
      } catch (error) {
        setMessageSeverity("error");
        setMessage(error?.message || "Failed to remove primary assignment.");
      } finally {
        setRemovingPrimaryClientId("");
      }
    },
    [plan?.name, planId, refreshCurrentAssignments],
  );

  const handleConfirm = React.useCallback(async () => {
    if (isLifecycleReadOnlyStatus) {
      setMessageSeverity("error");
      setMessage(
        `This version is ${planStatus}. Restore or create a draft version using the Manage button in the Training Plans toolbar before assigning.`,
      );
      return;
    }
    if (mode !== "alternative" && !selectedClientId) {
      setMessageSeverity("error");
      setMessage("Please select a client.");
      return;
    }
    if (mode !== "alternative" && !selectedModeEligibility.allowed) {
      setMessageSeverity("error");
      setMessage(selectedModeEligibility.reason || "This assignment mode is not allowed.");
      return;
    }
    setLoading(true);
    const normalizedCoachNote = coachNote.trim() || null;
    try {
      const createVariant = mode === "variant" || mode === "variant_draft";
      const variantAsDraft = mode === "variant_draft";
      const assignmentMode = mode === "alternative" ? "alternative" : "active";
      let publishedDuringAssign = false;
      let variantDraftPlanId = null;
      const targetClientIds =
        mode === "alternative"
          ? Array.from(new Set(selectedAlternativeClientIds))
          : [selectedClientId];
      if (mode === "alternative") {
        if (!targetClientIds.length) {
          setMessageSeverity("error");
          setMessage("Select at least one client for alternative assignment.");
          return;
        }
        const invalidTargetClientId = targetClientIds.find(
          (clientId) => !alternativeEligibilityByClientId[clientId]?.allowed,
        );
        if (invalidTargetClientId) {
          setMessageSeverity("error");
          setMessage(
            alternativeEligibilityByClientId[invalidTargetClientId]?.reason ||
              "One or more selected clients are not eligible for this alternative assignment.",
          );
          return;
        }
      } else if (!selectedClientId) {
        setMessageSeverity("error");
        setMessage("Please select a client.");
        return;
      }

      if (requiresPublishBeforeAssign) {
        const result = await publishAndAssignPlan({
          planId,
          clientIds: targetClientIds,
          mode: assignmentMode,
          createVariant,
          variantAsDraft,
          coachNote: normalizedCoachNote,
          sourcePlanName: plan?.name || null,
          clientsById,
        });
        publishedDuringAssign = !!result?.publishedDuringAssign;
        variantDraftPlanId = variantAsDraft ? result?.assignedPlanIds?.[0] || null : null;
        setMessageSeverity("success");
        setMessage(
          mode === "alternative"
            ? `Plan published and added as an alternative for ${targetClientIds.length} client(s).`
            : mode === "variant_draft"
              ? "Plan published. A variant draft was created for the selected client; their primary plan is unchanged."
              : "Plan published and assigned successfully.",
        );
      } else if (mode === "alternative") {
        await Promise.all(
          targetClientIds.map((clientId) =>
            assignPlanToClient({
              clientId,
              planId,
              mode: assignmentMode,
              createVariant,
              variantAsDraft: false,
              coachNote: normalizedCoachNote,
              sourcePlanName: plan?.name || null,
              clientNameSnapshot: clientsById?.[clientId]
                ? `${clientsById[clientId]?.firstName || ""} ${clientsById[clientId]?.lastName || ""}`.trim() ||
                  clientsById[clientId]?.uid ||
                  null
                : null,
            }),
          ),
        );
      } else {
        const assignedId = await assignPlanToClient({
          clientId: selectedClientId,
          planId,
          mode: assignmentMode,
          createVariant,
          variantAsDraft,
          coachNote: normalizedCoachNote,
          sourcePlanName: plan?.name || null,
          clientNameSnapshot: selectedClient
            ? `${selectedClient.firstName || ""} ${selectedClient.lastName || ""}`.trim() ||
              selectedClient.uid ||
              null
            : null,
        });
        if (variantAsDraft) {
          variantDraftPlanId = assignedId;
        }
      }
      await trackTrainingPlansV2Event("plan_assign", {
        planId,
        assignmentMode: mode,
        assignedClientCount: mode === "alternative" ? selectedAlternativeClientIds.length : 1,
        publishedDuringAssign,
      });
      if (!requiresPublishBeforeAssign) {
        setMessageSeverity("success");
        setMessage(
          mode === "alternative"
            ? `Training plan added as an alternative for ${selectedAlternativeClientIds.length} client(s).`
            : mode === "variant_draft"
              ? "Variant draft created for the selected client. Their primary plan is unchanged. Opening the draft in the editor."
              : "Training plan assigned successfully.",
        );
      }
      resetAssignmentFlow();
      await refreshCurrentAssignments();
      if (mode === "variant_draft" && variantDraftPlanId) {
        navigate(`/home/training-plans-v2/${variantDraftPlanId}/edit${location.search}`);
      }
    } catch (error) {
      setMessageSeverity("error");
      setMessage(error?.message || "Assignment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    alternativeEligibilityByClientId,
    mode,
    coachNote,
    clientsById,
    isLifecycleReadOnlyStatus,
    planStatus,
    plan?.name,
    selectedClient,
    planId,
    requiresPublishBeforeAssign,
    refreshCurrentAssignments,
    resetAssignmentFlow,
    selectedAlternativeClientIds.length,
    selectedClientId,
    selectedModeEligibility.allowed,
    selectedModeEligibility.reason,
    navigate,
    location.search,
  ]);

  return (
    <Box>
      {message ? (
        <Alert sx={{ mb: 2 }} severity={messageSeverity} onClose={() => setMessage("")}>
          {message}
        </Alert>
      ) : null}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Assign Training Plan</Typography>
        {hints.hasAnyDismissedInScope ? (
          <Button variant="text" size="small" onClick={hints.restoreAll}>
            Show hints
          </Button>
        ) : null}
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {plan ? `Plan: ${plan.name}` : "Loading plan..."}
      </Typography>
      {plan &&
      !isCrossOwnerClientPlanAssignment &&
      !hints.isDismissed("assign_plan_type_rules") ? (
        <Alert
          sx={{ mb: 2 }}
          severity="info"
          onClose={() => hints.dismiss("assign_plan_type_rules")}
        >
          {`${getPlanTypeLabel(plan.planType)} rules are enforced. Use versions for normal updates; reserve variants for special cross-client branching.`}
        </Alert>
      ) : null}
      {plan &&
      isCrossOwnerClientPlanAssignment &&
      !hints.isDismissed("assign_cross_client_copy") ? (
        <Alert
          sx={{ mb: 2 }}
          severity="info"
          onClose={() => hints.dismiss("assign_cross_client_copy")}
        >
          This plan is saved for <strong>{ownerClientLabel || "its client"}</strong>. To use it for{" "}
          <strong>
            {`${selectedClient?.firstName || ""} ${selectedClient?.lastName || ""}`.trim() ||
              selectedClientId}
          </strong>
          , choose a variant option in the next step — we create a separate copy for them.
        </Alert>
      ) : null}
      {requiresPublishBeforeAssign && !hints.isDismissed("assign_publish_before_assign") ? (
        <Alert
          sx={{ mb: 2 }}
          severity="warning"
          onClose={() => hints.dismiss("assign_publish_before_assign")}
        >
          This version is currently <strong>{planStatus}</strong>. It must be published before assignment.
          Confirming will run a Publish and Assign workflow.
        </Alert>
      ) : null}
      {isLifecycleReadOnlyStatus && !hints.isDismissed("assign_readonly_lifecycle") ? (
        <Alert
          sx={{ mb: 2 }}
          severity="warning"
          onClose={() => hints.dismiss("assign_readonly_lifecycle")}
        >
          This version is currently <strong>{planStatus}</strong> and is read-only. Use the Manage button
          in the Training Plans toolbar to restore or create a draft version before assigning.
        </Alert>
      ) : null}

      {plan ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={700} sx={{ mb: 0.5 }}>
            Who has this plan (this version)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Primary and alternative clients referencing this version&apos;s document. Add new assignments
            using the steps below. To <strong>remove a primary assignment</strong>, use{" "}
            <strong>Remove assignment</strong> in the primary list. To <strong>remove an alternative</strong> for
            someone who already has this plan as an alternative, use <strong>Remove alternative</strong> in the list —
            that does not change their primary plan.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
            <Chip
              size="small"
              variant="outlined"
              sx={getCoverageChipSx(currentPlanCoverage.primaryCount || 0, "success")}
              label={`Primary: ${currentPlanCoverage.primaryCount || 0}`}
            />
            <Chip
              size="small"
              variant="outlined"
              sx={getCoverageChipSx(currentPlanCoverage.alternativeCount || 0, "warning")}
              label={`Alternative: ${currentPlanCoverage.alternativeCount || 0}`}
            />
          </Stack>
          {primaryAssignments.length === 0 && alternativeAssignments.length === 0 ? (
            <Typography color="text.secondary">No client assignments yet.</Typography>
          ) : (
            <Stack spacing={1}>
              {primaryAssignments.length > 0 ? (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Primary assignments
                  </Typography>
                  <Stack spacing={1}>
                    {primaryAssignments.map((assignment) => (
                      <Paper
                        key={`primary-${assignment.identity || assignment.id}`}
                        variant="outlined"
                        sx={{ px: 1.5, py: 1 }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Typography variant="body2">{assignment.displayName}</Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={removingPrimaryClientId === assignment.identity}
                            onClick={() => handleRemovePrimaryAssignment(assignment)}
                            sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
                          >
                            {removingPrimaryClientId === assignment.identity
                              ? "Removing..."
                              : "Remove assignment"}
                          </Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ) : null}
              {alternativeAssignments.length > 0 ? (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Alternative assignments
                  </Typography>
                  <Stack spacing={1}>
                    {alternativeAssignments.map((assignment) => (
                      <Paper
                        key={`alternative-${assignment.identity || assignment.id}`}
                        variant="outlined"
                        sx={{ px: 1.5, py: 1 }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ sm: "center" }}
                          justifyContent="space-between"
                        >
                          <Typography variant="body2">{assignment.displayName}</Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={removingAlternativeClientId === assignment.identity}
                            onClick={() => handleRemoveAlternativeAssignment(assignment)}
                            sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
                          >
                            {removingAlternativeClientId === assignment.identity
                              ? "Removing..."
                              : "Remove alternative"}
                          </Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          )}
        </Paper>
      ) : null}

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Stack spacing={2}>
          <Autocomplete
            options={sortedClients}
            value={selectedClient}
            onChange={(_, value) => {
              setHasUserSelectedClient(true);
              setSelectedClientId(value?.uid || "");
            }}
            isOptionEqualToValue={(option, value) => option.uid === value.uid}
            getOptionLabel={(option) =>
              `${`${option.firstName || ""} ${option.lastName || ""}`.trim() || option.uid}${
                option.status === "disabled" || option.status === "inactive" ? " (inactive)" : ""
              }`
            }
            noOptionsText="No clients found"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Target Client"
                placeholder="Search by client name..."
              />
            )}
          />
        </Stack>
      )}

      {activeStep === 1 && (
        <Stack spacing={2}>
          <FormControl>
            <InputLabel>Assignment Mode</InputLabel>
            <Select
              label="Assignment Mode"
              value={availableModes.includes(mode) ? mode : ""}
              onChange={(e) => setMode(e.target.value)}
            >
              {availableModes.map((modeKey) => (
                <MenuItem key={modeKey} value={modeKey}>
                  {ASSIGNMENT_MODE_LABELS[modeKey] || modeKey}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {activeStep === 1 && selectedClientId && availableModes.length === 0 ? (
            <Alert severity="warning">
              No assignment mode is available for this client and plan. Go back and choose a different
              client, or open Manage to adjust the plan.
            </Alert>
          ) : null}
          {mode === "alternative" ? (
            <Autocomplete
              multiple
              options={eligibleAlternativeClients}
              value={selectedAlternativeClients}
              onChange={(_, value) =>
                setSelectedAlternativeClientIds(value.map((client) => client.uid))
              }
              isOptionEqualToValue={(option, value) => option.uid === value.uid}
              getOptionLabel={(option) =>
                `${option.firstName || ""} ${option.lastName || ""}`.trim() || option.uid
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clients to receive this as alternative"
                  placeholder="Select one or more clients"
                />
              )}
            />
          ) : null}
          {mode === "active" && !hints.isDismissed("assign_mode_impact_active") ? (
            <Alert severity="info" onClose={() => hints.dismiss("assign_mode_impact_active")}>
              Impact preview: This sets the client&apos;s primary `protocolId` and may reset split/report
              context.
            </Alert>
          ) : null}
          {mode === "alternative" && !hints.isDismissed("assign_mode_impact_alternative") ? (
            <Alert severity="info" onClose={() => hints.dismiss("assign_mode_impact_alternative")}>
              Impact preview: This adds the plan to each selected client&apos;s alternatives list and does
              not change their primary plan.
            </Alert>
          ) : null}
          {mode === "variant" && !hints.isDismissed("assign_mode_impact_variant") ? (
            <Alert severity="info" onClose={() => hints.dismiss("assign_mode_impact_variant")}>
              Impact preview: This creates a client-specific variant, then assigns it as that client&apos;s
              primary plan.
            </Alert>
          ) : null}
          {mode === "variant_draft" && !hints.isDismissed("assign_mode_impact_variant_draft") ? (
            <Alert severity="info" onClose={() => hints.dismiss("assign_mode_impact_variant_draft")}>
              Impact preview: This duplicates the plan for the selected client as a new draft. Their current
              primary plan stays in place until you publish the draft and set this copy as their active plan.
            </Alert>
          ) : null}
        </Stack>
      )}

      {activeStep === 2 && (
        <Stack spacing={2}>
          <Typography>
            Review: {plan?.name} will be assigned using mode{" "}
            {mode === "variant_draft"
              ? "Create variant as draft (primary unchanged)"
              : mode}{" "}
            to{" "}
            {mode === "alternative"
              ? `${selectedAlternativeClientIds.length} client(s)`
              : `${clientsById[selectedClientId]?.firstName || ""} ${clientsById[selectedClientId]?.lastName || ""}`.trim() ||
                "selected client"}
            .
          </Typography>
          {mode !== "variant_draft" && !hints.isDismissed("assign_review_split_context") ? (
            <Alert severity="info" onClose={() => hints.dismiss("assign_review_split_context")}>
              Assignment can reset split/report context depending on client state.
            </Alert>
          ) : null}
          {!hints.isDismissed("assign_review_mode_summary") ? (
            <Alert severity="info" onClose={() => hints.dismiss("assign_review_mode_summary")}>
              {mode === "alternative"
                ? `You are assigning this as an alternative to ${uniqueAlternativeTargets.length} client(s).`
                : mode === "variant"
                  ? "You are creating a special variant and assigning it as primary."
                  : mode === "variant_draft"
                    ? "You are creating a client-specific variant as a draft only. Publish it when ready, then set it as the client's active plan."
                    : "You are assigning this plan as the primary active plan."}
            </Alert>
          ) : null}
          <TextField
            label="Coach note (optional)"
            multiline
            minRows={3}
            value={coachNote}
            onChange={(e) => setCoachNote(e.target.value)}
          />
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          disabled={activeStep === 0}
          onClick={() => setActiveStep((step) => step - 1)}
        >
          Back
        </Button>
        {activeStep < 2 ? (
          <Button
            variant="contained"
            disabled={(activeStep === 0 && !isStepOneValid) || (activeStep === 1 && !isStepTwoValid)}
            onClick={() => setActiveStep((step) => step + 1)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={loading || !isReadyToConfirm || isLifecycleReadOnlyStatus}
            onClick={handleConfirm}
          >
            {loading
              ? requiresPublishBeforeAssign
                ? "Publishing and Assigning..."
                : "Assigning..."
              : isLifecycleReadOnlyStatus
                ? "Read-only status"
              : requiresPublishBeforeAssign
                ? "Publish and Assign"
                : "Confirm Assignment"}
          </Button>
        )}
        <Button
          variant="text"
          onClick={() => navigate(`/home/training-plans-v2/library${location.search}`)}
        >
          Close
        </Button>
      </Stack>
    </Box>
  );
};

export default TrainingPlansV2Assign;
